import { Controller } from "@hotwired/stimulus";
import { createPopper } from "@popperjs/core";
import { Loader } from "@googlemaps/js-api-loader";
import { Tooltip } from "bootstrap";

// Connects to data-controller="autocomplete"
export default class extends Controller {
  static values = {
    autocompleteReady: Boolean,
    eventListeners: Boolean,
    errorMessage: String,
    noResultsMessage: String,
  };

  static targets = [
    "inputWrapper",
    "resultTemplate",
    "popperWrapper",
    "searchInput",
    "searchResults",
    "locationInput",
    "localityInput",
    "administrativeAreaInput",
    "privateAddressInput",
    "postalCodeInput",
    "countryInput",
    "companyNameInput",
    "houseNumberInput",
    "postalCodeFormat",
    "postalCodeHint",
    "countryCode",
    "additionInput",
  ];

  initialize() {
    this.loader = new Loader({
      apiKey: "AIzaSyAti8pIn2LPbr_7uYCf6FJRFQQ6eZ7LQlw",
      version: "weekly",
    });

    this.loader.importLibrary("places").then(() => {
      this.geocoder = new google.maps.Geocoder();
      this.autocompleteReadyValue = true;
      this.results = {};
      this.fallbackFlag = "🏳️";
      this.sessionToken = new google.maps.places.AutocompleteSessionToken();
      this.boundCheckBoundsEvent = this.checkBounds.bind(this);
      this.boundHandleKeydownEvent = this.handleKeydown.bind(this);
      this.companyNameMaxLength = this.companyNameInputTarget.maxLength;
      this.locationMaxLength = this.locationInputTarget.maxLength;
      this.houseNumberMaxLength = this.houseNumberInputTarget.maxLength;
      this.additionMaxLength = this.additionInputTarget.maxLength;
      this.administrativeAreaMaxLength = this.administrativeAreaInputTarget.maxLength;
      this.popperInstance = createPopper(this.popperWrapperTarget, this.searchResultsTarget, {
        placement: "bottom-start",
        modifiers: [
          {
            name: "sameWidth",
            enabled: true,
            fn: (event) => {
              const state = event.state;
              state.styles.popper.width = `${state.rects.reference.width}px`;
            },
            phase: "beforeWrite",
            requires: ["computeStyles"],
          },
          {
            name: "flip",
            options: {
              padding: {
                top: 5,
              },
            },
          },
          {
            name: "offset",
            options: {
              offset: [0, 5],
            },
          },
        ],
      });

      this.inputTargetMap = ["locationInput", "localityInput", "postalCodeInput", "houseNumberInput", "administrativeAreaInput"];

      // Mapping to handle fallbacks for different address components, found is the variable that indicates if the component was found
      this.componentTypeMap = {
        locality: { target: "locality", found: "localityFound" },
        postal_town: { target: "locality", found: "localityFound" },
        sublocality: { target: "locality", found: "localityFound" },
        sublocality_level_1: { target: "locality", found: "localityFound" },
        sublocality_level_2: { target: "locality", found: "localityFound" },
        sublocality_level_3: { target: "locality", found: "localityFound" },
        sublocality_level_4: { target: "locality", found: "localityFound" },
        sublocality_level_5: { target: "locality", found: "localityFound" },
        administrative_area_level_1: { target: "administrativeArea", found: "administrativeAreaFound" },
        administrative_area_level_2: { target: "administrativeArea", found: "administrativeAreaFound" },
        administrative_area_level_3: { target: "administrativeArea", found: "administrativeAreaFound" },
        administrative_area_level_4: { target: "administrativeArea", found: "administrativeAreaFound" },
        administrative_area_level_5: { target: "administrativeArea", found: "administrativeAreaFound" },
        administrative_area_level_6: { target: "administrativeArea", found: "administrativeAreaFound" },
        administrative_area_level_7: { target: "administrativeArea", found: "administrativeAreaFound" },
        postal_code: { target: "postalCode", found: "postalCodeFound" },
        postal_code_prefix: { target: "postalCode", found: "postalCodeFound" },
        postal_code_suffix: { target: "postalCode", found: "postalCodeFound" },
        country: { target: "country", found: "countryFound" },
        street_number: { target: "houseNumber", found: "houseNumberFound" },
        route: { target: "location", found: "locationFound" },
        street_address: { target: "location", found: "locationFound" },
      };
    });

    this.initialPostalCodeHintText = this.postalCodeHintTarget.dataset.bsTitle || "";
    this.updateTooltip(this.postalCodeFormatTarget.innerHTML);
  }

  throttledSearch(event) {
    if (!this.autocompleteReadyValue) return;

    const value = event.currentTarget.value;

    clearTimeout(this.timeout);

    this.timeout = setTimeout(() => {
      if (value.length <= 1) {
        this.searchResultsTarget.innerHTML = "";
        this.results = {};
        return;
      }

      this.eventListenersValue = false;

      this.search(value);
    }, 300);
  }

  showResults() {
    if (Object.keys(this.results).length === 0) return; // No results are available, no need to show old results
    this.populateResults();
  }

  search(query) {
    this.inputWrapperTarget.classList.add("autocomplete--loading");

    const request = {
      input: query,
      region: this.countryInputTarget.dataset.iso3Code,
      language: document.documentElement.lang,
      sessionToken: this.sessionToken,
    };

    google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request).then((event) => {
      this.results = {};

      Promise.all(
        event.suggestions.map(async (suggestion, index) => {
          const placePrediction = suggestion.placePrediction;

          const { place } = await placePrediction.toPlace().fetchFields({
            fields: ["addressComponents", "formattedAddress", "location", "types"],
          });
          const country = place.addressComponents.find((component) => component.types.includes("country"));
          if (!country) return; // If no country is found, remove the result
          if (!this.countryInputTarget.querySelector(`option[data-code="${country.shortText}"]`)) return; // Country must be RiJo supported

          const isDuplicate = this.checkForDuplicate(place.formattedAddress);
          if (isDuplicate) return; // If the place is a duplicate, skip it

          const isOnlyCountry = place.addressComponents.length === 1 && place.addressComponents[0].types.includes("country");
          if (isOnlyCountry) return; // If the place is only a country, skip it

          const shouldGeocode = this.shouldGeoCode(place);

          const companyName = this.getCompanyName(placePrediction);

          this.results[index] = {
            displayName: companyName,
            addressComponents: place.addressComponents,
            formattedAddress: [companyName, place.formattedAddress].filter(Boolean).join(", "),
            resultText: this.setResultText(companyName, place.formattedAddress),
            addressType: this.setAddressType(place.types),
            flag: this.setCountryFlag(country),
            isDutch: country.shortText === "NL",
            shouldGeocode: shouldGeocode ? place : false,
          };
        })
      )
        .then(() => {
          this.sortResults(); // We want all Dutch addresses to be on top of the list
          this.populateResults();
        })
        .catch((error) => {
          window.dispatchEvent(new CustomEvent("flash:create", { detail: { type: "danger", message: this.errorMessageValue } }));
          console.error("Error fetching place details:", error);
        })
        .finally(() => {
          this.inputWrapperTarget.classList.remove("autocomplete--loading");
        });
    });
  }

  getCompanyName(placePrediction) {
    const isBusiness = ["establishment", "point_of_interest", "business"].some((type) => placePrediction.types.includes(type));

    return isBusiness ? placePrediction.mainText || "" : "";
  }

  checkForDuplicate(formattedAddress) {
    // Check if the formatted address already exists in the results
    return Object.values(this.results).some((result) => result.formattedAddress === formattedAddress);
  }

  shouldGeoCode(place) {
    const postalCode = place.addressComponents.find((component) => component.types.some((type) => ["postal_code", "postal_code_prefix", "postal_code_suffix"].includes(type)));
    if (!postalCode) return true;

    const street = place.addressComponents.find((component) => component.types.some((type) => ["route", "street_address"].includes(type)));
    if (!street) return true;

    const locality = place.addressComponents.find((component) =>
      component.types.some((type) =>
        ["locality", "postal_town", "sublocality", "sublocality_level_1", "sublocality_level_2", "sublocality_level_3", "sublocality_level_4", "sublocality_level_5"].includes(type)
      )
    );
    if (!locality) return true;

    const country = place.addressComponents.find((component) => component.types.includes("country"));
    // For Netherlands, only geocode if postal code format is invalid
    if (country.shortText === "NL") {
      if (postalCode.longText.replaceAll(" ", "").length !== 6) return true;
    }

    return false; // All necessary components are present, no need to geocode
  }

  sortResults() {
    const sortedResults = Object.values(this.results).sort((a, b) => {
      if (a.isDutch && !b.isDutch) {
        return -1; // A is Dutch, so it should come first
      }
      if (!a.isDutch && b.isDutch) {
        return 1; // B is Dutch, so it should come first
      }

      return 0; // When neither country is Dutch
    });

    this.results = {};

    sortedResults.forEach((result, index) => {
      this.results[index] = result;
    });
  }

  setResultText(displayName, formattedAddress) {
    let resultText = "";

    // Display name can be the same as a value in formattedAddress, so we avoid duplication
    if (displayName && !formattedAddress.includes(displayName)) {
      resultText = `${displayName}, ${formattedAddress}`;
    } else {
      resultText = formattedAddress;
    }

    return resultText;
  }

  setAddressType(types) {
    const isCompany = types && (types.includes("establishment") || types.includes("point_of_interest") || types.includes("store") || types.includes("business_building"));

    return isCompany ? "company" : "residential";
  }

  setCountryFlag(country) {
    const countryOption = this.countryInputTarget.querySelector(`option[data-code="${country.shortText}"]`);
    return countryOption ? countryOption.dataset.flag : this.fallbackFlag;
  }

  async geoCode(place, skipStreetAndNumberAutoFill = false) {
    return new Promise((resolve, reject) => {
      this.geocoder.geocode(
        {
          location: place.location,
        },
        (results, status) => {
          if (status === "OK" && results?.length) {
            const address = results[0];
            const newAddressComponents = [];

            // Get all the initial components for comparison
            const initialComponents = {};

            place.addressComponents.forEach((component) => {
              component.types.forEach((type) => {
                initialComponents[type] = component;
              });
            });

            // Filter out components that we'll handle manually
            let geocodedComponents = [...address.address_components];

            if (skipStreetAndNumberAutoFill) {
              geocodedComponents = geocodedComponents.filter((component) => !component.types.some((type) => ["route", "street_address", "street_number"].includes(type)));
            }

            // Process each geocoded component
            geocodedComponents.forEach((component) => {
              const initialComponent = initialComponents[component.types[0]];

              // If there's an initial component of this type, check if we should use it instead
              if (initialComponent) {
                // For postal code, use initial if it's longer/more specific or if same length but different
                if (component.types.some((type) => ["postal_code", "postal_code_prefix", "postal_code_suffix"].includes(type))) {
                  if (
                    initialComponent.longText.length > component.long_name.length ||
                    (initialComponent.longText.length === component.long_name.length && initialComponent.longText !== component.long_name)
                  ) {
                    newAddressComponents.push(initialComponent);
                    return; // Skip adding this geocoded component
                  }
                }
                // For all other component types, prefer the initial value
                else {
                  newAddressComponents.push(initialComponent);
                  return; // Skip adding this geocoded component
                }
              }

              // If we get here, use the geocoded component
              newAddressComponents.push({
                longText: component.long_name,
                shortText: component.short_name,
                types: component.types,
              });
            });

            // Make sure house number from initial place is included if it exists
            const initialHouseNumber = initialComponents["street_number"];
            if (initialHouseNumber && !newAddressComponents.some((component) => component.types.includes("street_number"))) {
              newAddressComponents.push(initialHouseNumber);
            }

            // if the newly geocoded does not contain the initial components, we add the initial components
            Object.values(initialComponents).forEach((component) => {
              // Only add this component if none of its types already exist in the new components
              const hasMatchingType = component.types.some((type) => newAddressComponents.some((newComponent) => newComponent.types.includes(type)));

              if (!hasMatchingType) {
                newAddressComponents.push(component);
              }
            });

            // TODO: It's still possible that the geocoded address does not contain all the necessary components (postal_code; for example: "London, UK")
            resolve(newAddressComponents);
          } else {
            reject(new Error(`Geocoding failed with status: ${status}`));
          }
        }
      );
    });
  }

  populateResults() {
    this.searchResultsTarget.innerHTML = "";
    this.popperInstance.update();

    if (Object.keys(this.results).length === 0) {
      window.dispatchEvent(new CustomEvent("flash:create", { detail: { type: "info", message: this.noResultsMessageValue } }));
      return;
    }

    this.eventListenersValue = true;

    Object.values(this.results).forEach((address, index) => {
      const addressOption = document.createElement("div");

      if (index === 0) {
        addressOption.classList.add("selected");
      }

      addressOption.classList.add("result");
      addressOption.addEventListener("click", () => this.selectResult(index));

      const addressEmoji = address.addressType === "company" ? "🏢" : "🏠";

      const resultTemplate = this.resultTemplateTarget.content.cloneNode(true);
      resultTemplate.querySelector(".address--emoji").textContent = addressEmoji;
      resultTemplate.querySelector(".address--text").textContent = address.resultText;
      resultTemplate.querySelector(".address--flag").textContent = address.flag;

      addressOption.appendChild(resultTemplate);

      this.searchResultsTarget.appendChild(addressOption);
    });
  }

  async selectResult(id) {
    const result = this.results[id];
    if (!result) return;

    let addressComponents;

    if (result.shouldGeocode) {
      this.inputWrapperTarget.classList.add("autocomplete--loading");
      // When the place is just a city or locality, we skip street and number autofill from geocoding

      // or if the street is known, but the number is not (e.g., "Amsterdam, Damstraat 1")
      // This is to prevent issues with geocoding when the address is just a city or locality
      let skipStreetAndNumberAutoFill =
        result.addressComponents.some((component) => component.types.includes("locality")) &&
        !result.addressComponents.some((component) => component.types.includes("street_number")) &&
        !result.addressComponents.some((component) => component.types.includes("route"));
      // or if the address is just a street without a number (e.g., "Damstraat, Amsterdam")
      if (
        result.addressComponents.some((component) => component.types.includes("route")) &&
        !result.addressComponents.some((component) => component.types.includes("street_number"))
      ) {
        skipStreetAndNumberAutoFill = true;
      }
      // or if the address is just a locality without a street (e.g., "Amsterdam")
      if (result.addressComponents.some((component) => component.types.includes("locality")) && !result.addressComponents.some((component) => component.types.includes("route"))) {
        skipStreetAndNumberAutoFill = true;
      }
      // or if the address is just a postal code without a street (e.g., "1012 AB")
      if (
        result.addressComponents.some((component) => component.types.includes("postal_code")) &&
        !result.addressComponents.some((component) => component.types.includes("route"))
      ) {
        skipStreetAndNumberAutoFill = true;
      }

      addressComponents = await this.geoCode(result.shouldGeocode, skipStreetAndNumberAutoFill);
      this.inputWrapperTarget.classList.remove("autocomplete--loading");
    } else {
      addressComponents = result.addressComponents;
    }

    const componentsFoundState = {
      localityFound: false,
      postalCodeFound: false,
      countryFound: false,
      houseNumberFound: false,
      locationFound: false,
      administrativeAreaFound: false,
    };

    let companyName = result.addressType === "company" ? result.displayName.toString() : "";

    if (companyName.length > this.companyNameMaxLength) {
      companyName = companyName.slice(0, this.companyNameMaxLength);
    }

    this.companyNameInputTarget.value = companyName;
    this.searchInputTarget.value = result.formattedAddress;
    this.searchResultsTarget.innerHTML = "";

    // TODO: Re-implement this if needed
    // this.privateAddressInputTarget.checked = result.addressType !== "company";

    addressComponents.forEach((component) => {
      const componentType = component.types[0];
      const mapping = this.componentTypeMap[componentType];

      if (mapping) {
        const { target, found } = mapping;
        if (target === "location" || target === "administrativeArea") {
          if (component.longText.toString() > this[`${target}MaxLength`]) {
            component.longText = component.longText.slice(0, this[`${target}MaxLength`]);
          }

          this[`${target}InputTarget`].value = component.longText;
        } else if (target === "country") {
          const selectedValue = this.countryInputTarget.querySelector(`option[data-code="${component.shortText}"]`).value;
          this.countryInputTarget.value = selectedValue;
          this.countryInputTarget.dispatchEvent(new Event("change"));
        } else if (target === "houseNumber") {
          let numericalValue = component.longText.replace(/\D/g, "");
          let additionValue = component.longText.replace(numericalValue, "").trim();

          if (numericalValue.length > this.houseNumberMaxLength) {
            numericalValue = numericalValue.slice(0, this.houseNumberMaxLength);
          }

          this.houseNumberInputTarget.value = numericalValue;

          if (additionValue) {
            if (additionValue.length > this.additionMaxLength) {
              additionValue = additionValue.slice(0, this.additionMaxLength);
            }

            this.additionInputTarget.value = additionValue;
          } else {
            this.additionInputTarget.value = "";
          }
        } else {
          this[`${target}InputTarget`].value = component.longText;
        }
        componentsFoundState[`${found}`] = true;
      }
    });

    const fields = [
      { found: componentsFoundState.localityFound, input: this.localityInputTarget },
      { found: componentsFoundState.postalCodeFound, input: this.postalCodeInputTarget },
      { found: componentsFoundState.countryFound, input: this.countryInputTarget },
      { found: componentsFoundState.houseNumberFound, input: this.houseNumberInputTarget },
      { found: componentsFoundState.locationFound, input: this.locationInputTarget },
      { found: componentsFoundState.administrativeAreaFound, input: this.administrativeAreaInputTarget },
    ];

    fields.forEach((field) => {
      if (!field.found) {
        field.input.value = "";
      }
    });
  }

  eventListenersValueChanged(value, oldValue) {
    if (value === oldValue) return;

    this.arrowKeysIndex = 0;

    if (value) {
      document.addEventListener("keydown", this.boundHandleKeydownEvent);
      document.addEventListener("click", this.boundCheckBoundsEvent);
    } else {
      document.removeEventListener("keydown", this.boundHandleKeydownEvent);
      document.removeEventListener("click", this.boundCheckBoundsEvent);
    }
  }

  checkBounds(event) {
    if (event.target === this.searchInputTarget) return;
    if (!this.searchResultsTarget.contains(event.target)) {
      this.searchResultsTarget.innerHTML = "";
      this.eventListenersValue = false;
    }
  }

  handleKeydown(event) {
    const key = event.key;

    if (!["ArrowDown", "ArrowUp", "Enter"].includes(key)) return;

    event.preventDefault();
    const maxIndex = this.searchResultsTarget.children.length - 1;

    if (key === "ArrowDown") {
      this.arrowKeysIndex = this.arrowKeysIndex === maxIndex ? 0 : this.arrowKeysIndex + 1;
    } else if (key === "ArrowUp") {
      this.arrowKeysIndex = this.arrowKeysIndex === 0 ? maxIndex : this.arrowKeysIndex - 1;
    } else if (key === "Enter") {
      this.searchResultsTarget.children[this.arrowKeysIndex]?.click();
    }

    Array.from(this.searchResultsTarget.children).forEach((child, index) => {
      child.classList.toggle("selected", index === this.arrowKeysIndex);
    });
  }

  updatedCountry(event) {
    const { options, selectedIndex } = event.currentTarget;
    const format = options[selectedIndex].dataset.postalCodeFormat;
    const code = options[selectedIndex].dataset.code;

    if (code) {
      this.countryCodeTarget.innerHTML = code;
      this.postalCodeInputTarget.dataset.localityAutocompleteCountryCodeParam = code;
    }

    if (format) {
      this.postalCodeFormatTarget.innerHTML = ` ${format}`;
    }

    this.postalCodeHintTarget.classList.toggle("opacity-0", !format);
    this.postalCodeHintTarget.classList.toggle("pe-none", !format);

    this.updateTooltip(format);
  }

  updateTooltip(format) {
    if (format) {
      this.postalCodeHintTarget.dataset.bsTitle = `[ ${format} ] - ${this.initialPostalCodeHintText}`;
    } else {
      this.postalCodeHintTarget.dataset.bsTitle = this.initialPostalCodeHintText;
    }

    Tooltip.getInstance(this.postalCodeHintTarget)?.dispose();

    new Tooltip(this.postalCodeHintTarget, {
      title: this.postalCodeHintTarget.dataset.bsTitle,
    });
  }
}
